<script setup lang="ts">
/**
 * MatchNavigationMockup — 赛事导航 mockup
 * v5: MatchList.vue + MatchCard.vue
 * 顶部「足球 | 篮球」运动切换（与真实控制台「赛事」分组同构）+ 联赛 Tab + 比赛列表
 * 首行展开（含回放/集锦 section，chip 含平台/分节），后 2 行折叠
 * 不显示任何比分，只 vs
 */
import { ref, watch } from 'vue'
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'

type Sport = 'football' | 'basketball'

interface VideoChip {
  platform: string
  platformName: string
  title: string
}

interface VideoSection {
  key: string
  label: string
  icon: IconName
  chips: VideoChip[]
}

interface MatchRow {
  league: string
  homeTeam: string
  awayTeam: string
  homeShort: string
  awayShort: string
  status: string
  date: string
  sections: VideoSection[]
}

const sport = ref<Sport>('football')
const expandedIdx = ref(0)

const sportTabs: { key: Sport; label: string; icon: IconName }[] = [
  { key: 'football', label: '足球', icon: 'ph:soccer-ball-bold' },
  { key: 'basketball', label: '篮球', icon: 'ph:basketball-bold' }
]

const config: Record<Sport, { tabs: string[]; matches: MatchRow[] }> = {
  football: {
    tabs: ['全部', '世界杯', '欧冠', '英超', '西甲', '意甲', '法甲', '德甲'],
    matches: [
      {
        league: '世界杯小组赛',
        homeTeam: '巴西',
        awayTeam: '法国',
        homeShort: 'BRA',
        awayShort: 'FRA',
        status: '未开赛',
        date: '11-25 03:00',
        sections: [
          {
            key: 'replay',
            label: '全场回放',
            icon: 'ph:play-circle-bold',
            chips: [
              { platform: 'cctv', platformName: '央视', title: '全场' },
              { platform: 'cctv', platformName: '央视', title: '上半场' },
              { platform: 'cctv', platformName: '央视', title: '下半场' }
            ]
          },
          {
            key: 'highlight',
            label: '比赛集锦',
            icon: 'ph:lightning-bold',
            chips: [
              { platform: 'migu', platformName: '咪咕', title: '官方集锦' },
              { platform: 'tencent', platformName: '腾讯', title: '5 分钟精华' }
            ]
          }
        ]
      },
      {
        league: '欧冠 1/4 决赛',
        homeTeam: '皇家马德里',
        awayTeam: '曼城',
        homeShort: 'RMA',
        awayShort: 'MNC',
        status: '未开赛',
        date: '11-26 03:00',
        sections: []
      },
      {
        league: '英超第 15 轮',
        homeTeam: '利物浦',
        awayTeam: '阿森纳',
        homeShort: 'LIV',
        awayShort: 'ARS',
        status: '未开赛',
        date: '11-23 20:30',
        sections: []
      }
    ]
  },
  basketball: {
    tabs: ['全部', 'NBA', 'CBA'],
    matches: [
      {
        league: 'NBA 季后赛',
        homeTeam: '湖人',
        awayTeam: '勇士',
        homeShort: 'LAL',
        awayShort: 'GSW',
        status: '未开赛',
        date: '04-14 10:30',
        sections: [
          {
            key: 'replay',
            label: '全场回放',
            icon: 'ph:play-circle-bold',
            chips: [
              { platform: 'tencent', platformName: '腾讯', title: '全场' },
              { platform: 'tencent', platformName: '腾讯', title: '第一节' },
              { platform: 'tencent', platformName: '腾讯', title: '第二节' },
              { platform: 'tencent', platformName: '腾讯', title: '第三节' },
              { platform: 'tencent', platformName: '腾讯', title: '第四节' }
            ]
          },
          {
            key: 'highlight',
            label: '比赛集锦',
            icon: 'ph:lightning-bold',
            chips: [
              { platform: 'migu', platformName: '咪咕', title: '官方集锦' }
            ]
          }
        ]
      },
      {
        league: 'CBA 总决赛',
        homeTeam: '辽宁',
        awayTeam: '广东',
        homeShort: 'LIA',
        awayShort: 'GDS',
        status: '未开赛',
        date: '04-13 19:35',
        sections: []
      },
      {
        league: 'NBA 常规赛',
        homeTeam: '凯尔特人',
        awayTeam: '独行侠',
        homeShort: 'BOS',
        awayShort: 'DAL',
        status: '未开赛',
        date: '04-12 08:00',
        sections: []
      }
    ]
  }
}

watch(sport, () => {
  expandedIdx.value = 0
})

function toggleExpand(idx: number) {
  expandedIdx.value = expandedIdx.value === idx ? -1 : idx
}
</script>

<template>
  <div class="mn-mock">
    <!-- 运动切换（与真实控制台「赛事」分组同构） -->
    <div class="mn-mock__sports" role="tablist" aria-label="运动类型">
      <button
        v-for="s in sportTabs"
        :key="s.key"
        type="button"
        role="tab"
        class="mn-mock__sport"
        :class="{ 'mn-mock__sport--active': sport === s.key }"
        :aria-selected="sport === s.key"
        @click="sport = s.key"
      >
        <YfbIcon :name="s.icon" :size="12" />
        <span>{{ s.label }}</span>
      </button>
    </div>

    <!-- 顶部赛事 Tab（展示用） -->
    <div class="mn-mock__tabs" aria-hidden="true">
      <span v-for="t in config[sport].tabs" :key="t" class="mn-mock__tab">{{ t }}</span>
    </div>

    <!-- 比赛列表 -->
    <div class="mn-mock__list">
      <article
        v-for="(m, idx) in config[sport].matches"
        :key="`${sport}-${idx}`"
        class="mn-mock__row"
        :class="{ 'mn-mock__row--expanded': expandedIdx === idx }"
      >
        <!-- 折叠行（始终可见） -->
        <button
          type="button"
          class="mn-mock__head yfb-press"
          :aria-expanded="expandedIdx === idx"
          @click="toggleExpand(idx)"
        >
          <span class="mn-mock__league">{{ m.league }}</span>
          <span class="mn-mock__teams">
            <span class="mn-mock__team">{{ m.homeTeam }}</span>
            <span class="mn-mock__vs">VS</span>
            <span class="mn-mock__team">{{ m.awayTeam }}</span>
          </span>
          <span class="mn-mock__date">{{ m.date }}</span>
          <span class="mn-mock__arrow" aria-hidden="true">
            <YfbIcon
              :name="expandedIdx === idx ? 'lucide:chevron-down' : 'lucide:chevron-right'"
              :size="14"
            />
          </span>
        </button>

        <!-- 展开行（视频 sections） -->
        <div v-if="expandedIdx === idx && m.sections.length > 0" class="mn-mock__body">
          <div
            v-for="(sec, sidx) in m.sections"
            :key="sidx"
            class="mn-mock__section"
          >
            <div class="mn-mock__section-head">
              <span class="mn-mock__section-tag">
                <YfbIcon :name="sec.icon" :size="11" />
                <span>{{ sec.label }}</span>
              </span>
            </div>
            <div class="mn-mock__chips">
              <button
                v-for="(chip, cidx) in sec.chips"
                :key="cidx"
                type="button"
                class="mn-mock__chip yfb-press"
              >
                <span class="mn-mock__chip-platform">{{ chip.platformName }}</span>
                <span class="mn-mock__chip-sep">·</span>
                <span class="mn-mock__chip-title">{{ chip.title }}</span>
              </button>
            </div>
          </div>
        </div>
      </article>
    </div>

    <div class="mn-mock__footer">
      <YfbIcon name="lucide:shield-check" :size="12" />
      <span>隐分宝 · 比分已隐藏 · 视频来源一览无余</span>
    </div>
  </div>
</template>

<style scoped>
.mn-mock {
  border: 1px solid var(--yfb-rule);
  background-color: var(--yfb-surface);
  box-shadow: var(--yfb-shadow-2);
  border-radius: var(--yfb-radius-lg);
  overflow: hidden;
}

/* 运动切换 */
.mn-mock__sports {
  display: flex;
  gap: 1px;
  background-color: var(--yfb-rule);
  border-bottom: 1px solid var(--yfb-rule);
}

.mn-mock__sport {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px;
  background-color: var(--yfb-surface-2);
  border: none;
  color: var(--yfb-text-secondary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: var(--yfb-ls-button);
  cursor: pointer;
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

.mn-mock__sport:hover {
  color: var(--yfb-ink);
}

.mn-mock__sport--active {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

/* Tabs */
.mn-mock__tabs {
  display: flex;
  border-bottom: 1px solid var(--yfb-rule);
  background-color: var(--yfb-surface-2);
  overflow-x: auto;
}

.mn-mock__tab {
  padding: 8px 12px;
  border-right: 1px solid var(--yfb-rule-soft);
  font-family: var(--yfb-font-heading);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  color: var(--yfb-text-secondary);
  white-space: nowrap;
}

/* 列表 */
.mn-mock__list {
  display: flex;
  flex-direction: column;
}

.mn-mock__row {
  border-bottom: 1px solid var(--yfb-rule);
}

.mn-mock__row:last-child {
  border-bottom: none;
}

/* 折叠行 */
.mn-mock__head {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
  width: 100%;
  padding: var(--yfb-sp-4);
  background-color: var(--yfb-surface);
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: inherit;
  transition: background-color var(--yfb-anim-fast) ease-out;
}

.mn-mock__head:hover {
  background-color: var(--yfb-surface-2);
}

.mn-mock__row--expanded .mn-mock__head {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.mn-mock__league {
  flex-shrink: 0;
  padding: 3px 8px;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-font-heading);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
}

.mn-mock__row--expanded .mn-mock__league {
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border-color: var(--yfb-accent);
}

.mn-mock__teams {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(var(--yfb-sp-2) + var(--yfb-sp-1) / 2);
  min-width: 0;
}

.mn-mock__team {
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mn-mock__vs {
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: var(--yfb-ls-eyebrow);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  flex-shrink: 0;
}

.mn-mock__row--expanded .mn-mock__vs {
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border-color: var(--yfb-accent);
}

.mn-mock__date {
  flex-shrink: 0;
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  color: inherit;
  opacity: 0.85;
}

.mn-mock__arrow {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* 展开行 */
.mn-mock__body {
  background-color: var(--yfb-surface-2);
  padding: var(--yfb-sp-4);
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-4);
  border-top: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.mn-mock__section {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-2);
}

.mn-mock__section-head {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
}

.mn-mock__section-tag {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-1);
  padding: 3px 8px;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border-radius: var(--yfb-radius-sm);
}

.mn-mock__chips {
  display: flex;
  flex-wrap: wrap;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
}

.mn-mock__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-1);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  background-color: var(--yfb-surface);
  color: var(--yfb-ink);
  border: 1px solid var(--yfb-rule);
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
  border-radius: var(--yfb-radius-sm);
  transition: background-color var(--yfb-anim-fast) ease-out;
}

.mn-mock__chip:hover {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.mn-mock__chip-platform {
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
}

.mn-mock__chip-sep {
  opacity: 0.5;
}

.mn-mock__chip-title {
  white-space: nowrap;
}

/* Footer */
.mn-mock__footer {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border-top: 1px solid var(--yfb-rule);
}

@media (max-width: 767px) {
  .mn-mock__teams {
    flex-wrap: wrap;
  }

  .mn-mock__date {
    font-size: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mn-mock__sport,
  .mn-mock__head,
  .mn-mock__chip {
    transition: none;
  }
}
</style>
